import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { animate, stagger } from 'animejs';
import useIntersectionObserver from '../../hooks/useIntersectionObserver';
import useAuth from '../../hooks/use-auth';
import AuthModal from '../../components/auth-modal';
import ChatbotWidget from '../../components/chatbot-widget';
import { Carousel } from '../home/index';
import {
    ArrowRight,
    Bot,
    Briefcase,
    Building2,
    Check,
    Factory,
    GitBranch,
    Headset,
    Layers,
    MountainSnow,
    Network,
    Palette,
    Plug,
    ShieldCheck,
    TrendingUp,
    UserCheck,
    Users,
    Workflow,
    Wrench,
} from 'lucide-react';
import {
    HomeGlobalStyle,
    Page,
    PageBackground,
    HeroGlow,
    PageContent,
    TopMenu,
    TopMenuInner,
    TopMenuLogo,
    TopMenuBrand,
    TopMenuBrandText,
    TopMenuNav,
    TopMenuList,
    TopMenuItem,
    TopMenuLink,
    TopMenuArrow,
    TopMenuDropdown,
    TopMenuDropdownLink,
    AuthButtonWrapper,
    AuthButton,
    Hero,
    HeroBadge,
    HeroTitle,
    HeroSubtitle,
    BadgeRow,
    Badge,
    CTAWrapper,
    CTAPrimary,
    CTASecondary,
    HeroNote,
    Section,
    SectionEyebrow,
    SectionTitle,
    SectionSubtitle,
    IconTile,
    DemoFrame,
    DemoWindow,
    DemoBar,
    LogoStrip,
    FeatureGrid,
    FeatureCard,
    ArchitectureGrid,
    ArchitectureCard,
    UseCaseGrid,
    UseCaseCard,
    AgentLinkGrid,
    AgentLinkCard,
    StepsSection,
    StepsInner,
    StepList,
    StepItem,
    FooterCTA,
    FooterCTATitle,
    FooterCTASubtitle,
    FooterCTAButton,
    FooterBottom,
} from './style.css.js';
import ChatAnimation from '../../components/chat-animation/index.js';
import DotGridBackground from '../../components/dot-grid-background';
import SEO from '../../components/seo';
import { DEFAULT_META, GEO, HOME_JSON_LD } from '../../constants/seo';

const MESSAGES = [
    {
        id: '1',
        message: 'Bonjour ! Je suis l\'orchestrateur Synaptek. Décrivez votre besoin — je route vers l\'agent ou l\'outil adapté.',
        timestamp: '14:00',
        type: 'bot',
    },
    {
        id: '2',
        message: 'J\'ai une panne sur une référence produit et je voudrais aussi un créneau SAV.',
        timestamp: '14:00',
        type: 'user',
    },
    {
        id: '3',
        message: 'Je passe la main à l\'agent SAV pour le diagnostic, puis à l\'agent commercial pour le rendez-vous. Un instant…',
        timestamp: '14:01',
        type: 'bot',
    },
    {
        id: '4',
        message: 'D\'après la notice, vérifiez le fusible F3. Si le défaut persiste, je propose un créneau technicien demain matin.',
        timestamp: '14:01',
        type: 'bot',
    },
    {
        id: '5',
        message: 'Oui, demain matin me convient.',
        timestamp: '14:02',
        type: 'user',
    },
    {
        id: '6',
        message: 'Créneau réservé à 9h30. Un humain de votre équipe peut reprendre le fil à tout moment.',
        timestamp: '14:02',
        type: 'bot',
    },
];

const DEMO_AVATAR_SRC = '/assets/avatar-macarons-shop.png';

const NewHomePage = () => {
    const { user, updateFn, logout } = useAuth();
    const [authModalOpen, setAuthModalOpen] = useState(false);
    const [demoAvatarError, setDemoAvatarError] = useState(false);
    const [sectionRef, isSectionVisible] = useIntersectionObserver({ threshold: 0.15 });
    const [footerRef, isFooterVisible] = useIntersectionObserver({ threshold: 0.2 });

    const authInitial = user?.email ? user.email.charAt(0).toUpperCase() : '?';

    useEffect(() => {
        animate('.new-home .hero-logo', {
            opacity: 1,
            translateY: 0,
            duration: 500,
            easing: 'easeOutCubic',
        });
        animate('.new-home .hero-badge', {
            opacity: 1,
            translateY: 0,
            duration: 500,
            easing: 'easeOutCubic',
            delay: 100,
        });
        animate('.new-home .hero-title', {
            opacity: 1,
            translateY: 0,
            duration: 550,
            easing: 'easeOutCubic',
            delay: 220,
        });
        animate('.new-home .hero-subtitle', {
            opacity: 1,
            translateY: 0,
            duration: 500,
            easing: 'easeOutCubic',
            delay: 320,
        });
        animate('.new-home .hero-badges .badge-item', {
            opacity: 1,
            translateY: 0,
            duration: 450,
            easing: 'easeOutCubic',
            delay: stagger(80, { start: 440 }),
        });
        animate('.new-home .hero-cta', {
            opacity: 1,
            translateY: 0,
            duration: 500,
            easing: 'easeOutCubic',
            delay: 720,
        });
        animate('.new-home .hero-note', {
            opacity: 1,
            translateY: 0,
            duration: 400,
            easing: 'easeOutCubic',
            delay: 820,
        });
    }, []);

    useEffect(() => {
        if (!isSectionVisible) return;
        animate('.new-home .section-eyebrow, .new-home .section-title', {
            opacity: 1,
            translateY: 0,
            duration: 500,
            easing: 'easeOutCubic',
        });
        animate('.new-home .section-subtitle', {
            opacity: 1,
            translateY: 0,
            duration: 500,
            easing: 'easeOutCubic',
            delay: 100,
        });
        animate('.new-home .feature-card', {
            opacity: 1,
            translateY: 0,
            duration: 500,
            easing: 'easeOutCubic',
            delay: stagger(90, { start: 220 }),
        });
    }, [isSectionVisible]);

    useEffect(() => {
        if (!isFooterVisible) return;
        animate('.new-home .footer-cta-title', {
            opacity: 1,
            translateY: 0,
            duration: 500,
            easing: 'easeOutCubic',
        });
        animate('.new-home .footer-cta-subtitle', {
            opacity: 1,
            translateY: 0,
            duration: 500,
            easing: 'easeOutCubic',
            delay: 120,
        });
        animate('.new-home .footer-cta-btn', {
            opacity: 1,
            translateY: 0,
            duration: 500,
            easing: 'easeOutCubic',
            delay: 260,
        });
    }, [isFooterVisible]);

    return (
        <Page>
            <HomeGlobalStyle />
            <SEO
                title={DEFAULT_META.title}
                description={DEFAULT_META.description}
                keywords={DEFAULT_META.keywords}
                canonical="/"
                jsonLd={HOME_JSON_LD}
                geo={GEO}
            />
            <DotGridBackground as={PageBackground} />
            <PageContent className="new-home">
            <TopMenu>
                <TopMenuInner>
                    <Link to="/" aria-label="Accueil Synaptek">
                        <TopMenuBrand>
                            <TopMenuLogo src="/assets/logo-white-500.png" alt="Synaptek" />
                            <TopMenuBrandText>synaptek</TopMenuBrandText>
                        </TopMenuBrand>
                    </Link>
                    <TopMenuNav aria-label="Navigation principale">
                        <TopMenuList>
                            <TopMenuItem>
                                <TopMenuLink as={Link} to="/solutions/orchestrateur-multi-agents">
                                    Solutions <TopMenuArrow aria-hidden>▾</TopMenuArrow>
                                </TopMenuLink>
                                <TopMenuDropdown>
                                    <TopMenuDropdownLink as={Link} to="/solutions/orchestrateur-multi-agents">
                                        Orchestrateur multi-agents
                                    </TopMenuDropdownLink>
                                    <TopMenuDropdownLink as={Link} to="/solutions/support-client-24-7">
                                        Agent support client
                                    </TopMenuDropdownLink>
                                    <TopMenuDropdownLink as={Link} to="/solutions/agent-commercial">
                                        Agent commercial
                                    </TopMenuDropdownLink>
                                    <TopMenuDropdownLink as={Link} to="/solutions/sav-technique">
                                        Agent SAV technique
                                    </TopMenuDropdownLink>
                                    <TopMenuDropdownLink as={Link} to="/solutions/support-interne">
                                        Agent support interne
                                    </TopMenuDropdownLink>
                                    <TopMenuDropdownLink as={Link} to="/solutions/creation-visuelle-ia">
                                        Agent création visuelle
                                    </TopMenuDropdownLink>
                                </TopMenuDropdown>
                            </TopMenuItem>
                            <TopMenuItem>
                                <TopMenuLink as={Link} to="/secteurs">
                                    Secteurs <TopMenuArrow aria-hidden>▾</TopMenuArrow>
                                </TopMenuLink>
                                <TopMenuDropdown>
                                    <TopMenuDropdownLink as={Link} to="/secteurs/tourisme-loisirs">Tourisme & Loisirs</TopMenuDropdownLink>
                                    <TopMenuDropdownLink as={Link} to="/secteurs/industrie">Industrie</TopMenuDropdownLink>
                                    <TopMenuDropdownLink as={Link} to="/secteurs/services-immobilier">Services & Immobilier</TopMenuDropdownLink>
                                </TopMenuDropdown>
                            </TopMenuItem>
                            <TopMenuItem>
                                <TopMenuLink as={Link} to="/securite">Sécurité</TopMenuLink>
                            </TopMenuItem>
                            <TopMenuItem>
                                <TopMenuLink as={Link} to="/tarifs">Tarifs</TopMenuLink>
                            </TopMenuItem>
                            <TopMenuItem>
                                <TopMenuLink as={Link} to="/blog">Blog</TopMenuLink>
                            </TopMenuItem>
                        </TopMenuList>
                    </TopMenuNav>
                </TopMenuInner>
            </TopMenu>
            <AuthButtonWrapper>
                <AuthButton
                    type="button"
                    onClick={() => setAuthModalOpen(true)}
                    $loggedIn={!!user}
                    aria-label={user ? 'Mon compte' : 'Connexion'}
                >
                    {user ? (
                        authInitial
                    ) : demoAvatarError ? (
                        'M'
                    ) : (
                        <img
                            src={DEMO_AVATAR_SRC}
                            alt="Connexion"
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            onError={() => setDemoAvatarError(true)}
                        />
                    )}
                </AuthButton>
            </AuthButtonWrapper>
            <AuthModal
                isOpen={authModalOpen}
                onClose={() => setAuthModalOpen(false)}
                user={user}
                onLogin={updateFn}
                onLogout={logout}
            />
            <Hero>
                <HeroGlow aria-hidden />
                <div className="hero-logo" data-animate>
                    <div className="hero-logo-ghost">
                        <img src="/assets/logo-white-500.png" alt="Logo Synaptek" />
                    </div>
                </div>
                <HeroBadge className="hero-badge" data-animate>
                    <span className="dot" aria-hidden />
                    Synaptek — Conception d&apos;orchestrateurs multi-agents · Annecy &amp; Haute-Savoie
                </HeroBadge>
                <HeroTitle className="hero-title" data-animate>
                    Conception d&apos;orchestrateurs multi&#8209;agents
                    <span className="accent">pour votre métier, vos outils et vos équipes.</span>
                </HeroTitle>
                <HeroSubtitle className="hero-subtitle" data-animate>
                    Au-delà d&apos;un chatbot isolé : un orchestrateur qui route vers des agents spécialisés,
                    branche vos outils et votre SI, et laisse l&apos;humain reprendre la main quand c&apos;est
                    nécessaire — sur-mesure pour les entreprises d&apos;Annecy, de la Haute-Savoie et de l&apos;axe Annecy–Genève.
                </HeroSubtitle>

                <BadgeRow className="hero-badges">
                    <Badge className="badge-item" data-animate><Check aria-hidden />Orchestrateur + agents spécialisés</Badge>
                    <Badge className="badge-item" data-animate><Check aria-hidden />Outils &amp; SI connectés</Badge>
                    <Badge className="badge-item" data-animate><Check aria-hidden />Human-in-the-loop</Badge>
                </BadgeRow>

                <CTAWrapper className="hero-cta" data-animate>
                    <CTAPrimary as={Link} to="/onboarding">
                        Lancer un projet d&apos;orchestration
                        <ArrowRight aria-hidden />
                    </CTAPrimary>
                    <CTASecondary as={Link} to="/solutions/orchestrateur-multi-agents">
                        Voir l&apos;offre
                    </CTASecondary>
                </CTAWrapper>
                <HeroNote className="hero-note" data-animate>
                    Cadrage sur-mesure — Essai possible sans CB
                </HeroNote>
            </Hero>

            <Section $flushTop aria-label="Démonstration de l'orchestrateur">
                <DemoFrame>
                <DemoWindow>
                <DemoBar aria-hidden>
                    <i /><i /><i />
                    <span>Démo — orchestrateur Synaptek</span>
                </DemoBar>
                <ChatAnimation
                    darkTheme
                    users={[
                        {
                            id: '1',
                            name: 'John Doe',
                            avatar: 'https://via.placeholder.com/150',
                        },
                    ]}
                    messages={MESSAGES}
                />
                </DemoWindow>
                </DemoFrame>
            </Section>

            <Section ref={sectionRef}>
                <SectionEyebrow className="section-eyebrow" data-animate>Architecture</SectionEyebrow>
                <SectionTitle className="section-title" data-animate>
                    Un système multi-agents, pas un agent unique
                </SectionTitle>
                <SectionSubtitle className="section-subtitle" data-animate>
                    L&apos;orchestration d&apos;agents IA coordonne les rôles, les outils et la supervision humaine
                    pour traiter des parcours métier complets.
                </SectionSubtitle>
                <ArchitectureGrid>
                    <ArchitectureCard className="feature-card" data-animate>
                        <IconTile aria-hidden><Network /></IconTile>
                        <span className="arch-label">Cœur</span>
                        <h3>Orchestrateur</h3>
                        <p>
                            Comprend l&apos;intention, choisit le parcours, délègue aux bons agents et
                            consolide la réponse — avec règles métier et garde-fous définis avec vous.
                        </p>
                    </ArchitectureCard>
                    <ArchitectureCard className="feature-card" data-animate>
                        <IconTile aria-hidden><Bot /></IconTile>
                        <span className="arch-label">Spécialistes</span>
                        <h3>Agents métier</h3>
                        <p>
                            Support client, commercial, SAV, RH, création visuelle… chaque agent est calibré
                            sur ses contenus, son ton et ses objectifs.
                        </p>
                    </ArchitectureCard>
                    <ArchitectureCard className="feature-card" data-animate>
                        <IconTile aria-hidden><Plug /></IconTile>
                        <span className="arch-label">Connexions</span>
                        <h3>Outils &amp; SI</h3>
                        <p>
                            CRM, calendrier, ERP, base documentaire, messagerie : l&apos;orchestrateur agit
                            dans votre écosystème, pas hors-sol.
                        </p>
                    </ArchitectureCard>
                    <ArchitectureCard className="feature-card" data-animate>
                        <IconTile aria-hidden><UserCheck /></IconTile>
                        <span className="arch-label">Contrôle</span>
                        <h3>Human-in-the-loop</h3>
                        <p>
                            Escalade structurée, validation sur les cas sensibles, reprise du fil par vos
                            équipes — vous gardez la main.
                        </p>
                    </ArchitectureCard>
                </ArchitectureGrid>
            </Section>

            <StepsSection>
                <StepsInner>
                    <SectionEyebrow className="section-eyebrow" data-animate>Méthode</SectionEyebrow>
                    <SectionTitle className="section-title" data-animate>
                        De la conception à la mise en production
                    </SectionTitle>
                    <SectionSubtitle className="section-subtitle" data-animate>
                        Une démarche de conception d&apos;orchestrateurs multi-agents, pas un template générique.
                    </SectionSubtitle>
                    <StepList>
                        <StepItem>
                            <div>
                                <h3>Cadrage métier</h3>
                                <p>
                                    Parcours clients et internes, sources de vérité, règles d&apos;escalade,
                                    contraintes réglementaires et SI existants.
                                </p>
                            </div>
                        </StepItem>
                        <StepItem>
                            <div>
                                <h3>Architecture multi-agents</h3>
                                <p>
                                    Conception de l&apos;orchestrateur, des agents spécialisés et des outils
                                    à brancher — avec responsabilités claires pour chacun.
                                </p>
                            </div>
                        </StepItem>
                        <StepItem>
                            <div>
                                <h3>Intégration &amp; garde-fous</h3>
                                <p>
                                    Connexion aux bases de connaissances et aux APIs, tests de parcours,
                                    supervision humaine et journalisation.
                                </p>
                            </div>
                        </StepItem>
                        <StepItem>
                            <div>
                                <h3>Déploiement &amp; itération</h3>
                                <p>
                                    Mise en production progressive, mesure qualitative des conversations,
                                    ajustement des agents et de l&apos;orchestration.
                                </p>
                            </div>
                        </StepItem>
                    </StepList>
                </StepsInner>
            </StepsSection>

            <Section>
                <SectionEyebrow className="section-eyebrow" data-animate>Secteurs</SectionEyebrow>
                <SectionTitle className="section-title" data-animate>
                    Pensé pour l&apos;économie locale : Annecy, Haute-Savoie, Alpes
                </SectionTitle>
                <SectionSubtitle className="section-subtitle" data-animate>
                    Des systèmes multi-agents sur-mesure pour les réalités du bassin Annecy–Genève,
                    du tourisme alpin et de l&apos;industrie de la Vallée de l&apos;Arve.
                </SectionSubtitle>
                <UseCaseGrid>
                    <UseCaseCard>
                        <IconTile aria-hidden><MountainSnow /></IconTile>
                        <h3>Tourisme &amp; loisirs</h3>
                        <p>
                            Orchestration entre information 24/7, qualification de séjour et prise de
                            rendez-vous — pour hôtels, hébergements et activités en Haute-Savoie.
                        </p>
                        <Link to="/secteurs/tourisme-loisirs">Voir le secteur <ArrowRight aria-hidden /></Link>
                    </UseCaseCard>
                    <UseCaseCard>
                        <IconTile aria-hidden><Factory /></IconTile>
                        <h3>Industrie &amp; décolletage</h3>
                        <p>
                            Agents SAV et catalogues techniques coordonnés avec vos notices, partenaires
                            et flux B2B — typiques de la Vallée de l&apos;Arve.
                        </p>
                        <Link to="/secteurs/industrie">Voir le secteur <ArrowRight aria-hidden /></Link>
                    </UseCaseCard>
                    <UseCaseCard>
                        <IconTile aria-hidden><Building2 /></IconTile>
                        <h3>Services &amp; immobilier</h3>
                        <p>
                            Qualification de leads, présentation d&apos;offres et agenda partagé — adaptés
                            aux cabinets et agences de l&apos;axe Annecy–Genève.
                        </p>
                        <Link to="/secteurs/services-immobilier">Voir le secteur <ArrowRight aria-hidden /></Link>
                    </UseCaseCard>
                </UseCaseGrid>
            </Section>

            <Section>
                <SectionEyebrow className="section-eyebrow" data-animate>Solutions</SectionEyebrow>
                <SectionTitle className="section-title" data-animate>
                    Des agents spécialisés dans votre orchestrateur
                </SectionTitle>
                <SectionSubtitle className="section-subtitle" data-animate>
                    Chaque solution Synaptek peut fonctionner seule ou comme brique d&apos;un système multi-agents.
                </SectionSubtitle>
                <AgentLinkGrid>
                    <AgentLinkCard as={Link} to="/solutions/orchestrateur-multi-agents" $featured>
                        <IconTile aria-hidden><Workflow /></IconTile>
                        <span className="agent-body">
                            <strong>Orchestrateur multi-agents</strong>
                            <span className="agent-desc">La pièce centrale : conception &amp; déploiement du système.</span>
                        </span>
                        <ArrowRight className="agent-arrow" aria-hidden />
                    </AgentLinkCard>
                    <AgentLinkCard as={Link} to="/solutions/support-client-24-7">
                        <IconTile aria-hidden><Headset /></IconTile>
                        <span className="agent-body">
                            <strong>Agent support client</strong>
                            <span className="agent-desc">Questions récurrentes, suivi, orientation — sous l&apos;orchestrateur.</span>
                        </span>
                        <ArrowRight className="agent-arrow" aria-hidden />
                    </AgentLinkCard>
                    <AgentLinkCard as={Link} to="/solutions/agent-commercial">
                        <IconTile aria-hidden><Briefcase /></IconTile>
                        <span className="agent-body">
                            <strong>Agent commercial</strong>
                            <span className="agent-desc">Qualification, objections, prise de rendez-vous.</span>
                        </span>
                        <ArrowRight className="agent-arrow" aria-hidden />
                    </AgentLinkCard>
                    <AgentLinkCard as={Link} to="/solutions/sav-technique">
                        <IconTile aria-hidden><Wrench /></IconTile>
                        <span className="agent-body">
                            <strong>Agent SAV technique</strong>
                            <span className="agent-desc">Diagnostic guidé et escalade structurée.</span>
                        </span>
                        <ArrowRight className="agent-arrow" aria-hidden />
                    </AgentLinkCard>
                    <AgentLinkCard as={Link} to="/solutions/support-interne">
                        <IconTile aria-hidden><Users /></IconTile>
                        <span className="agent-body">
                            <strong>Agent support interne</strong>
                            <span className="agent-desc">RH, IT et procédures pour vos collaborateurs.</span>
                        </span>
                        <ArrowRight className="agent-arrow" aria-hidden />
                    </AgentLinkCard>
                    <AgentLinkCard as={Link} to="/solutions/creation-visuelle-ia">
                        <IconTile aria-hidden><Palette /></IconTile>
                        <span className="agent-body">
                            <strong>Agent création visuelle</strong>
                            <span className="agent-desc">Production visuelle assistée, pilotée par le brief.</span>
                        </span>
                        <ArrowRight className="agent-arrow" aria-hidden />
                    </AgentLinkCard>
                </AgentLinkGrid>
            </Section>

            <Section>
                <SectionEyebrow className="section-eyebrow" data-animate>Intégrations</SectionEyebrow>
                <SectionTitle className="section-title" data-animate>
                    Intégration à votre stack
                </SectionTitle>
                <SectionSubtitle className="section-subtitle" data-animate>
                    L&apos;orchestration d&apos;agents IA reste dans votre écosystème d&apos;outils.
                </SectionSubtitle>
                <LogoStrip>
                    <Carousel />
                </LogoStrip>
            </Section>

            <Section>
                <SectionEyebrow className="section-eyebrow" data-animate>Pourquoi Synaptek</SectionEyebrow>
                <SectionTitle className="section-title" data-animate>
                    Pourquoi un orchestrateur plutôt qu&apos;un seul chatbot ?
                </SectionTitle>
                <FeatureGrid>
                    <FeatureCard className="feature-card" data-animate>
                        <IconTile aria-hidden><GitBranch /></IconTile>
                        <h3>Parcours complexes</h3>
                        <p>
                            Un même fil peut enchaîner diagnostic, devis et rendez-vous : plusieurs
                            expertises, une seule expérience pour l&apos;utilisateur.
                        </p>
                    </FeatureCard>
                    <FeatureCard className="feature-card" data-animate>
                        <IconTile aria-hidden><Layers /></IconTile>
                        <h3>Spécialisation maîtrisée</h3>
                        <p>
                            Chaque agent reste expert de son domaine ; l&apos;orchestrateur évite les
                            réponses hors-sujet et les collisions de règles.
                        </p>
                    </FeatureCard>
                    <FeatureCard className="feature-card" data-animate>
                        <IconTile aria-hidden><ShieldCheck /></IconTile>
                        <h3>Gouvernance</h3>
                        <p>
                            Traçabilité, escalade humaine et politiques d&apos;accès aux outils : conçues
                            dès le cadrage, pas en afterthought.
                        </p>
                    </FeatureCard>
                    <FeatureCard className="feature-card" data-animate>
                        <IconTile aria-hidden><TrendingUp /></IconTile>
                        <h3>Évolutif</h3>
                        <p>
                            Ajoutez un agent ou une connexion SI sans tout reconstruire — le système
                            multi-agents grandit avec votre organisation.
                        </p>
                    </FeatureCard>
                </FeatureGrid>
            </Section>

            <FooterCTA ref={footerRef}>
                <FooterCTATitle className="footer-cta-title" data-animate>
                    Concevoir votre orchestrateur multi-agents ?
                </FooterCTATitle>
                <FooterCTASubtitle className="footer-cta-subtitle" data-animate>
                    Expliquez-nous vos parcours, vos sources de données et vos contraintes SI :
                    nous cadrons un système multi-agents sur-mesure — Annecy, Haute-Savoie, Alpes.
                </FooterCTASubtitle>
                <FooterCTAButton className="footer-cta-btn" data-animate as={Link} to="/onboarding">
                    Démarrer un projet d&apos;orchestration
                    <ArrowRight aria-hidden />
                </FooterCTAButton>
            </FooterCTA>

            <FooterBottom>
                © {new Date().getFullYear()} Synaptek — Conception d&apos;orchestrateurs multi-agents · Annecy
            </FooterBottom>

            <ChatbotWidget slug="synaptek-annecy" />
            </PageContent>
        </Page>
    );
};

export default NewHomePage;
